import React, { useMemo, useState } from "react";
import PageHeader from "../components/common/PageHeader";
import useStore from "../store/useStore";
import { Assessment, Print } from "@mui/icons-material";
import reportService from "../services/reportService";

const REPORT_TYPES = [
  { value: "overview", label: "Platform Overview" },
  { value: "companies", label: "Companies Report" },
  { value: "subscriptions", label: "Subscriptions Report" },
  { value: "users", label: "Company Users Report" },
  { value: "administrators", label: "System Administrators Report" }
];

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB");
};

const formatMoney = (value, currency = "GMD") =>
  `${currency} ${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const yesNo = (value) => (value ? "Yes" : "No");
const activeLabel = (value) => (value ? "Active" : "Inactive");

const tableRowsHtml = (rows, cells, emptyCols) => {
  if (!rows.length) {
    return `<tr><td colspan="${emptyCols}" class="empty">No records found.</td></tr>`;
  }
  return rows.map((row) => `<tr>${cells(row)}</tr>`).join("");
};

const SuperAdminReports = () => {
  const { toast, user } = useStore();
  const [reportType, setReportType] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState("");

  const metrics = useMemo(() => reportData?.metrics || {}, [reportData?.metrics]);
  const companies = reportData?.companies || [];
  const plans = reportData?.plans || [];
  const planBreakdown = reportData?.planBreakdown || [];
  const users = reportData?.users || [];
  const administrators = reportData?.administrators || [];
  const key = reportData?.reportKey;

  const showOverview = key === "overview";
  const showCompanies = key === "overview" || key === "companies" || key === "subscriptions";
  const showSubscriptions = key === "overview" || key === "subscriptions";
  const showUsers = key === "overview" || key === "users";
  const showAdmins = key === "overview" || key === "administrators";

  const summaryCards = useMemo(() => {
    if (!reportData) return [];
    if (key === "companies") {
      return [
        { label: "Companies", value: metrics.totalCompanies },
        { label: "Active Companies", value: metrics.activeCompanies },
        { label: "Inactive Companies", value: metrics.inactiveCompanies }
      ];
    }
    if (key === "subscriptions") {
      return [
        { label: "Active Subscriptions", value: metrics.activeSubscriptions },
        { label: "Inactive", value: metrics.inactiveSubscriptions },
        { label: "Suspended", value: metrics.suspendedSubscriptions },
        { label: "Subscription Revenue", value: formatMoney(metrics.subscriptionRevenue) }
      ];
    }
    if (key === "users") {
      return [
        { label: "Company Users", value: metrics.companyUsers },
        { label: "Company Admins", value: metrics.companyAdmins },
        { label: "Staff", value: metrics.staffUsers },
        { label: "Viewers", value: metrics.viewerUsers }
      ];
    }
    if (key === "administrators") {
      return [
        { label: "System Administrators", value: metrics.systemAdministrators },
        { label: "Total Users", value: metrics.totalUsers }
      ];
    }
    return [
      { label: "Companies", value: metrics.totalCompanies },
      { label: "Users", value: metrics.totalUsers },
      { label: "Active Subscriptions", value: metrics.activeSubscriptions },
      { label: "Subscription Revenue", value: formatMoney(metrics.subscriptionRevenue) },
      { label: "Assets", value: metrics.totalAssets },
      { label: "Tenants", value: metrics.totalTenants },
      { label: "Occupancy", value: `${metrics.occupancyRate || 0}%` },
      { label: "Tenant Payments", value: formatMoney(metrics.tenantPaymentRevenue) }
    ];
  }, [key, metrics, reportData]);

  const handleGenerateReport = async () => {
    setReportError("");
    if (!reportType) {
      toast("Please select a report type", "error");
      return;
    }

    try {
      setLoading(true);
      const data = await reportService.generatePlatformReport(reportType);
      if (!data) {
        throw new Error("No report data returned");
      }
      setReportData(data);
      toast("Report generated successfully", "success");
    } catch (error) {
      console.error("Failed to generate platform report:", error);
      const message = error.response?.data?.message || "Failed to generate platform report";
      setReportError(message);
      setReportData(null);
      toast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!reportData) return;

    const reportWindow = window.open("", "_blank", "width=1100,height=800");
    if (!reportWindow) {
      toast("Please allow pop-ups to print the report", "error");
      return;
    }

    const printDateTime = new Date(reportData.generatedAt || new Date()).toLocaleString("en-GB");
    const printDate = new Date(reportData.generatedAt || new Date()).toLocaleDateString("en-GB");
    const title = escapeHtml(reportData.type || "Platform Report");

    const statsHtml = summaryCards
      .map((card) => `<div class="stat">${escapeHtml(card.label)}<span>${escapeHtml(card.value)}</span></div>`)
      .join("");

    const companiesHtml = showCompanies ? `
      <h3 class="section-title">Companies</h3>
      <table>
        <thead>
          <tr>
            <th>Company</th>
            <th>Contact</th>
            <th>Status</th>
            <th>Plan</th>
            <th>Cycle</th>
            <th class="right">Amount</th>
            <th>Start</th>
            <th>End</th>
            <th class="right">Users</th>
            <th class="right">Assets</th>
          </tr>
        </thead>
        <tbody>
          ${tableRowsHtml(companies, (row) => `
            <td>${escapeHtml(row.companyName)}</td>
            <td>${escapeHtml(row.email || "-")}<br />${escapeHtml(row.phone || "")}</td>
            <td>${escapeHtml(row.subscriptionStatus)} / ${escapeHtml(activeLabel(row.active))}</td>
            <td>${escapeHtml(row.subscriptionPlan || "-")}</td>
            <td>${escapeHtml(row.subscriptionCycle || "-")}</td>
            <td class="right">${escapeHtml(formatMoney(row.subscriptionAmount, row.subscriptionCurrency))}</td>
            <td>${escapeHtml(formatDate(row.subscriptionStart))}</td>
            <td>${escapeHtml(formatDate(row.subscriptionEnd))}</td>
            <td class="right">${row.userCount ?? 0}</td>
            <td class="right">${row.assetCount ?? 0}</td>
          `, 10)}
        </tbody>
      </table>
    ` : "";

    const plansHtml = showSubscriptions ? `
      <h3 class="section-title">Subscription Plans</h3>
      <table>
        <thead>
          <tr>
            <th>Plan</th>
            <th>Description</th>
            <th class="right">Monthly</th>
            <th class="right">Yearly</th>
            <th>Featured</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          ${tableRowsHtml(plans, (row) => `
            <td>${escapeHtml(row.name)}</td>
            <td>${escapeHtml(row.description || "-")}</td>
            <td class="right">${escapeHtml(formatMoney(row.monthlyPrice, row.currency))}</td>
            <td class="right">${escapeHtml(formatMoney(row.yearlyPrice, row.currency))}</td>
            <td>${escapeHtml(yesNo(row.featured))}</td>
            <td>${escapeHtml(activeLabel(row.active))}</td>
          `, 6)}
        </tbody>
      </table>
      <h3 class="section-title">Plan Assignment</h3>
      <table>
        <thead>
          <tr>
            <th>Plan</th>
            <th class="right">Companies</th>
            <th class="right">Assigned Amount</th>
          </tr>
        </thead>
        <tbody>
          ${tableRowsHtml(planBreakdown, (row) => `
            <td>${escapeHtml(row.plan)}</td>
            <td class="right">${row.companyCount ?? 0}</td>
            <td class="right">${escapeHtml(formatMoney(row.totalAmount))}</td>
          `, 3)}
        </tbody>
      </table>
    ` : "";

    const usersHtml = showUsers ? `
      <h3 class="section-title">Company Users</h3>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Company</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          ${tableRowsHtml(users, (row) => `
            <td>${escapeHtml(`${row.firstName || ""} ${row.lastName || ""}`.trim() || "-")}</td>
            <td>${escapeHtml(row.email || "-")}</td>
            <td>${escapeHtml(row.role || "-")}</td>
            <td>${escapeHtml(row.companyName || "-")}</td>
            <td>${escapeHtml(formatDate(row.createdAt))}</td>
          `, 5)}
        </tbody>
      </table>
    ` : "";

    const adminsHtml = showAdmins ? `
      <h3 class="section-title">System Administrators</h3>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          ${tableRowsHtml(administrators, (row) => `
            <td>${escapeHtml(`${row.firstName || ""} ${row.lastName || ""}`.trim() || "-")}</td>
            <td>${escapeHtml(row.email || "-")}</td>
            <td>${escapeHtml(row.role || "-")}</td>
            <td>${escapeHtml(formatDate(row.createdAt))}</td>
          `, 4)}
        </tbody>
      </table>
    ` : "";

    reportWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          @page { size: A4 landscape; margin: 12mm; }
          * { box-sizing: border-box; }
          body { margin: 0; font-family: Georgia, 'Times New Roman', serif; color: #111; background: #fff; }
          .page { max-width: 1080px; margin: 0 auto; padding: 8px 6px 20px; }
          .company { text-align: center; margin-bottom: 18px; }
          .company-name { font-size: 19px; font-weight: 700; text-transform: uppercase; line-height: 1.15; }
          .company-address, .company-contact { font-size: 11px; margin-top: 2px; }
          .report-title { text-align: center; font-size: 17px; font-weight: 700; margin: 16px 0 2px; }
          .report-subtitle { text-align: center; font-size: 12px; margin-bottom: 8px; }
          .report-date { text-align: right; font-size: 11px; margin: 0 0 14px; }
          .section-title { font-size: 13px; font-weight: 700; margin: 18px 0 8px; text-transform: uppercase; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 11px; }
          th, td { border: 1px solid #222; padding: 6px 7px; vertical-align: top; }
          thead th { background: #ececec; }
          .right { text-align: right; }
          .empty { text-align: center; padding: 12px; }
          .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 8px 0 16px; }
          .stat { border: 1px solid #222; padding: 8px 10px; background: #f8f8f8; font-size: 11px; font-weight: 700; }
          .stat span { display: block; margin-top: 4px; font-size: 13px; }
          .verified { margin-top: 28px; font-size: 11px; }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="company">
            <div class="company-name">GAMRENT</div>
            <div class="company-address">Platform administration report</div>
            <div class="company-contact">Prepared by: ${escapeHtml(user?.email || "SuperAdmin")}</div>
          </div>
          <div class="report-title">${title}</div>
          <div class="report-subtitle">Generated on ${escapeHtml(printDateTime)}</div>
          <div class="report-date">Date: ${escapeHtml(printDate)}</div>
          <div class="stats-grid">${statsHtml}</div>
          ${companiesHtml}
          ${plansHtml}
          ${usersHtml}
          ${adminsHtml}
          <div class="verified">Verified by: ____________________</div>
        </div>
      </body>
      </html>
    `);
    reportWindow.document.close();
    reportWindow.focus();
    reportWindow.print();
    reportWindow.close();
  };

  return (
    <div className="report-page">
      <div className="print-hide">
        <PageHeader title="Platform Reports" />
      </div>

      <div className="print-hide bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-6 text-center">
          Select a platform report to generate
        </h2>

        <div className="max-w-md mx-auto space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Report Type: <span className="text-red-500">*</span>
            </label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              {REPORT_TYPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleGenerateReport}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400"
          >
            <Assessment />
            {loading ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </div>

      {reportData && (
        <div className="report-print-area bg-white rounded-xl border border-gray-200 shadow-sm p-8 mb-8">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 uppercase">GAMRENT</h2>
              <p className="text-sm text-gray-600 mt-1">Platform administration report</p>
              <p className="text-sm text-gray-600">Prepared by: {user?.email || "SuperAdmin"}</p>
            </div>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors print:hidden"
            >
              <Print />
              Print Report
            </button>
          </div>

          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">{reportData.type}</h3>
            <p className="text-sm text-gray-500 mt-1">
              Generated on {new Date(reportData.generatedAt).toLocaleString()}
            </p>
          </div>

          {reportError && (
            <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <strong>Report API error:</strong> {reportError}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            {summaryCards.map((card) => (
              <div key={card.label} className="border border-gray-200 p-4 text-sm font-semibold">
                {card.label}
                <span className="mt-2 block text-lg font-bold">{card.value}</span>
              </div>
            ))}
          </div>

          {showOverview && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-8">
              <div className="border border-gray-200 p-4 text-sm font-semibold">Company Users<span className="mt-2 block text-lg font-bold">{metrics.companyUsers || 0}</span></div>
              <div className="border border-gray-200 p-4 text-sm font-semibold">System Administrators<span className="mt-2 block text-lg font-bold">{metrics.systemAdministrators || 0}</span></div>
              <div className="border border-gray-200 p-4 text-sm font-semibold">Rentals<span className="mt-2 block text-lg font-bold">{metrics.totalRentals || 0}</span></div>
              <div className="border border-gray-200 p-4 text-sm font-semibold">Maintenance<span className="mt-2 block text-lg font-bold">{metrics.totalMaintenance || 0}</span></div>
            </div>
          )}

          {showCompanies && (
            <section className="mb-8">
              <h4 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-3">Companies</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-3 py-2 text-left">Company</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Contact</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Status</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Plan</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Cycle</th>
                      <th className="border border-gray-200 px-3 py-2 text-right">Amount</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Period</th>
                      <th className="border border-gray-200 px-3 py-2 text-right">Users</th>
                      <th className="border border-gray-200 px-3 py-2 text-right">Assets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="border border-gray-200 px-3 py-4 text-center text-gray-500">No companies found.</td>
                      </tr>
                    ) : companies.map((row) => (
                      <tr key={row.id}>
                        <td className="border border-gray-200 px-3 py-2">{row.companyName}</td>
                        <td className="border border-gray-200 px-3 py-2">{row.email || "-"}<br />{row.phone || ""}</td>
                        <td className="border border-gray-200 px-3 py-2">{row.subscriptionStatus} / {activeLabel(row.active)}</td>
                        <td className="border border-gray-200 px-3 py-2">{row.subscriptionPlan || "-"}</td>
                        <td className="border border-gray-200 px-3 py-2">{row.subscriptionCycle || "-"}</td>
                        <td className="border border-gray-200 px-3 py-2 text-right">{formatMoney(row.subscriptionAmount, row.subscriptionCurrency)}</td>
                        <td className="border border-gray-200 px-3 py-2">{formatDate(row.subscriptionStart)} – {formatDate(row.subscriptionEnd)}</td>
                        <td className="border border-gray-200 px-3 py-2 text-right">{row.userCount}</td>
                        <td className="border border-gray-200 px-3 py-2 text-right">{row.assetCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {showSubscriptions && (
            <>
              <section className="mb-8">
                <h4 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-3">Subscription Plans</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-200 px-3 py-2 text-left">Plan</th>
                        <th className="border border-gray-200 px-3 py-2 text-left">Description</th>
                        <th className="border border-gray-200 px-3 py-2 text-right">Monthly</th>
                        <th className="border border-gray-200 px-3 py-2 text-right">Yearly</th>
                        <th className="border border-gray-200 px-3 py-2 text-left">Featured</th>
                        <th className="border border-gray-200 px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plans.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="border border-gray-200 px-3 py-4 text-center text-gray-500">No subscription plans found.</td>
                        </tr>
                      ) : plans.map((row) => (
                        <tr key={row.id}>
                          <td className="border border-gray-200 px-3 py-2">{row.name}</td>
                          <td className="border border-gray-200 px-3 py-2">{row.description || "-"}</td>
                          <td className="border border-gray-200 px-3 py-2 text-right">{formatMoney(row.monthlyPrice, row.currency)}</td>
                          <td className="border border-gray-200 px-3 py-2 text-right">{formatMoney(row.yearlyPrice, row.currency)}</td>
                          <td className="border border-gray-200 px-3 py-2">{yesNo(row.featured)}</td>
                          <td className="border border-gray-200 px-3 py-2">{activeLabel(row.active)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mb-8">
                <h4 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-3">Plan Assignment</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-200 px-3 py-2 text-left">Plan</th>
                        <th className="border border-gray-200 px-3 py-2 text-right">Companies</th>
                        <th className="border border-gray-200 px-3 py-2 text-right">Assigned Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planBreakdown.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="border border-gray-200 px-3 py-4 text-center text-gray-500">No plan assignments found.</td>
                        </tr>
                      ) : planBreakdown.map((row) => (
                        <tr key={row.plan}>
                          <td className="border border-gray-200 px-3 py-2">{row.plan}</td>
                          <td className="border border-gray-200 px-3 py-2 text-right">{row.companyCount}</td>
                          <td className="border border-gray-200 px-3 py-2 text-right">{formatMoney(row.totalAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          {showUsers && (
            <section className="mb-8">
              <h4 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-3">Company Users</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-3 py-2 text-left">Name</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Email</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Role</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Company</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="border border-gray-200 px-3 py-4 text-center text-gray-500">No company users found.</td>
                      </tr>
                    ) : users.map((row) => (
                      <tr key={row.id}>
                        <td className="border border-gray-200 px-3 py-2">{`${row.firstName || ""} ${row.lastName || ""}`.trim() || "-"}</td>
                        <td className="border border-gray-200 px-3 py-2">{row.email}</td>
                        <td className="border border-gray-200 px-3 py-2">{row.role}</td>
                        <td className="border border-gray-200 px-3 py-2">{row.companyName || "-"}</td>
                        <td className="border border-gray-200 px-3 py-2">{formatDate(row.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {showAdmins && (
            <section className="mb-8">
              <h4 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-3">System Administrators</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-3 py-2 text-left">Name</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Email</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Role</th>
                      <th className="border border-gray-200 px-3 py-2 text-left">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {administrators.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="border border-gray-200 px-3 py-4 text-center text-gray-500">No system administrators found.</td>
                      </tr>
                    ) : administrators.map((row) => (
                      <tr key={row.id}>
                        <td className="border border-gray-200 px-3 py-2">{`${row.firstName || ""} ${row.lastName || ""}`.trim() || "-"}</td>
                        <td className="border border-gray-200 px-3 py-2">{row.email}</td>
                        <td className="border border-gray-200 px-3 py-2">{row.role}</td>
                        <td className="border border-gray-200 px-3 py-2">{formatDate(row.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default SuperAdminReports;
